import React, { useEffect, useMemo, useRef, useState } from 'react';
import FavoriteButton from './FavoriteButton.js';
import { getListingImage } from './listingPlaceholder.js';
import { scoreToColor, scoreInkColor } from './areaData.js';
import { scoreWord } from './scoreWords.js';
import {
    parseAreaValue,
    getListingSqmPriceValue,
    formatListingPrice,
    normalizeListingType,
    normalizeListingScore,
} from './ListViewPage.js';

// The listings rail on the left of kartvy's split view. It shows the same homes the map draws (the client
// -filtered set), as compact cards. Hovering a card lights its map marker and vice versa (onHover /
// hoveredId) and focuses its area on the map (onHover gets the listing; Map.js shows the area's hover look
// and stats card), and clicking a card opens the listing popup (onOpen). Each card's "Visa på kartan" button
// (onLocate) flies the map to the home; the card then reads "Visas på kartan" (focusedId). With several areas,
// each area's header folds its homes away.
// Rendered only on desktop widths; the map stays full-bleed on phones. Cards reuse listvy's formatters so
// the two views read the same.

// The rail shows this many at a time and auto-loads the next batch when the bottom comes into view.
const PAGE_STEP = 50;

const LocateIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z" />
        <circle cx="12" cy="10" r="2.3" />
    </svg>
);
const CheckIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
);

const KartvyListCard = React.memo(function KartvyListCard({ listing, swedish, isHovered, isFocused, onHover, onOpen, onLocate, areaStats }) {
    const type = normalizeListingType(listing.type, { swedish });
    const areaValue = parseAreaValue(listing);
    const rooms = listing.rooms ? `${listing.rooms} ${swedish ? 'rum' : 'rooms'}` : '';
    const areaText = areaValue != null ? `${Math.round(areaValue)} m²` : '';
    // Type and rooms on one line; the m² size goes on the next row so the narrow text column doesn't clip it.
    const meta = [type, rooms].filter(Boolean).join(' · ');
    const sqmPrice = getListingSqmPriceValue(listing);
    const sqmText = Number.isFinite(sqmPrice) && sqmPrice > 0
        ? `${sqmPrice.toLocaleString('sv-SE').replace(/ /g, ' ')} kr/m²`
        : '';

    const score = normalizeListingScore(listing.betyg ?? (listing.regsoId && areaStats ? areaStats[`combined_${listing.regsoId}`] : null));
    const scoreColor = score != null ? scoreInkColor(scoreToColor[score] || '#8f9aab') : '#8f9aab';
    const scoreLabel = score == null ? '–' : (Number.isInteger(score) ? `${score}` : score.toFixed(1).replace('.', swedish ? ',' : '.'));
    const stars = score == null ? 0 : Math.max(1, Math.min(5, Math.round((score / 6) * 5)));

    return (
        <article
            className={`kartvy-list-card${isHovered ? ' is-hovered' : ''}`}
            onMouseEnter={() => onHover(String(listing.id), listing)}
            onMouseLeave={() => onHover(null, null)}
            onClick={() => onOpen(listing)}
        >
            <div className="kartvy-list-card-media">
                <img
                    src={getListingImage(listing)}
                    alt={listing.address || listing.title || ''}
                    loading="lazy"
                    draggable={false}
                />
                <FavoriteButton
                    listing={listing}
                    swedish={swedish}
                    source="kartvy_list"
                    className="kartvy-list-card-favorite"
                />
                {listing.isNewConstruction && (
                    <span className="kartvy-list-card-new">{swedish ? 'Nyproduktion' : 'New build'}</span>
                )}
            </div>
            <div className="kartvy-list-card-body">
                <div className="kartvy-list-card-head">
                    <h3>{listing.address || listing.title}</h3>
                </div>
                <span className="kartvy-list-card-price">
                    {formatListingPrice(listing, swedish ? 'Pris saknas' : 'Price missing')}
                </span>
                {meta && <p className="kartvy-list-card-meta">{meta}</p>}
                {areaText && <p className="kartvy-list-card-meta kartvy-list-card-size">{areaText}</p>}
                <div className="kartvy-list-card-foot">
                    {score != null && (
                        <span className="kartvy-list-card-score">
                            <span className="kartvy-list-card-score-value" style={{ color: scoreColor }}>{scoreLabel}</span>
                            <span className="kartvy-list-card-stars" aria-hidden="true">
                                {Array.from({ length: 5 }, (_, index) => (
                                    <span
                                        key={index}
                                        className={index < stars ? 'is-active' : ''}
                                        style={index < stars ? { color: scoreColor } : undefined}
                                    >★</span>
                                ))}
                            </span>
                            <span className="kartvy-list-card-score-word">{scoreWord(score, swedish)}</span>
                        </span>
                    )}
                    {sqmText && <span className="kartvy-list-card-sqm">{sqmText}</span>}
                </div>
                {Number.isFinite(Number(listing.lat)) && Number.isFinite(Number(listing.lng)) && (
                    <button
                        type="button"
                        className={`kartvy-list-card-locate${isFocused ? ' is-active' : ''}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            onLocate(listing);
                        }}
                    >
                        {isFocused ? <CheckIcon /> : <LocateIcon />}
                        {isFocused
                            ? (swedish ? 'Visas på kartan' : 'Shown on the map')
                            : (swedish ? 'Visa på kartan' : 'Show on map')}
                    </button>
                )}
            </div>
        </article>
    );
});

const ChevronIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 9l6 6 6-6" />
    </svg>
);

const HeartIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />
    </svg>
);

const formatCount = (n, swedish) => Number(n || 0).toLocaleString(swedish ? 'sv-SE' : 'en-US').replace(/\s/g, ' ');

// The rail's tabs (Map.js's listTab), like listvy's: for sale, coming soon and favourites, each with its count.
const TABS = [
    { id: 'for_sale', sv: 'Till salu', en: 'For sale' },
    { id: 'coming_soon', sv: 'Kommande', en: 'Coming soon' },
    { id: 'favorites', sv: 'Favoriter', en: 'Favourites', heart: true },
];
const EMPTY_TEXT = {
    for_sale: { sv: 'Inga bostäder till salu i de valda områdena.', en: 'No homes for sale in the selected areas.' },
    coming_soon: { sv: 'Inga kommande bostäder i de valda områdena.', en: 'No homes coming soon in the selected areas.' },
    favorites: {
        sv: 'Inga favoriter ännu. Tryck på hjärtat på en bostad för att spara den här.',
        en: 'No favourites yet. Tap the heart on a home to save it here.',
    },
};

const KartvyListPanel = ({
    open = true, listings, groups, swedish, hoveredId, focusedId, onHover, onHoverArea, onOpen, onLocate, onFoldArea, areaStats,
    tab = 'for_sale', tabCounts = null, onTab = null,
}) => {
    const [limit, setLimit] = useState(PAGE_STEP);
    // Areas whose homes are folded away under their header (by group key).
    const [folded, setFolded] = useState(() => new Set());
    const toggleFold = (key) => {
        setFolded((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key); else next.add(key);
            onFoldArea?.(next.has(key));
            return next;
        });
    };
    const scrollRef = useRef(null);
    const sentinelRef = useRef(null);
    const total = listings.length;

    // A new search/filter result starts the rail back at the top and the first page.
    const signature = useMemo(() => `${tab}:${listings.slice(0, 3).map((l) => l.id).join('|')}:${total}`, [listings, total, tab]);
    useEffect(() => {
        setLimit(PAGE_STEP);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [signature]);

    // Hovering a map marker highlights its card here (the data-listing match below), but must NOT scroll the
    // rail to it — that made the whole list column jump under the pointer. A card hover is already in view, so
    // no scroll is needed either way; the highlight alone syncs both directions.

    // When more than one area is picked, show the homes grouped per area with a divider before each group;
    // otherwise a flat list. Either way, paginate by the number of cards shown (limit).
    const grouped = Array.isArray(groups) && groups.length > 1;
    // Folded areas' homes aren't shown, so they don't count towards the page either.
    const groupedTotal = grouped
        ? groups.reduce((sum, group) => sum + (folded.has(group.key) ? 0 : group.listings.length), 0)
        : total;
    const effectiveTotal = grouped ? groupedTotal : total;
    const hasMore = limit < effectiveTotal;

    // Lazy-load the next batch when the bottom sentinel comes into view (instead of a "Visa fler" button),
    // capped at PAGE_STEP more each time.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        const root = scrollRef.current;
        if (!sentinel || !root || !hasMore) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setLimit((current) => Math.min(current + PAGE_STEP, effectiveTotal));
            }
        }, { root, rootMargin: '400px' });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, effectiveTotal, limit]);

    const rows = [];
    if (grouped) {
        let count = 0;
        for (const group of groups) {
            if (count >= limit) break;
            rows.push({ divider: group });
            if (folded.has(group.key)) continue;
            for (const listing of group.listings) {
                if (count >= limit) break;
                rows.push({ listing });
                count += 1;
            }
        }
    } else {
        (limit >= total ? listings : listings.slice(0, limit)).forEach((listing) => rows.push({ listing }));
    }

    const renderCard = (listing) => (
        <div key={listing.id} data-listing={String(listing.id)}>
            <KartvyListCard
                listing={listing}
                swedish={swedish}
                isHovered={hoveredId === String(listing.id)}
                isFocused={focusedId === String(listing.id)}
                onHover={onHover}
                onOpen={onOpen}
                onLocate={onLocate}
                areaStats={areaStats}
            />
        </div>
    );

    return (
        <aside className={`kartvy-list-rail${open ? ' is-open' : ''}`} aria-hidden={!open} aria-label={swedish ? 'Bostäder' : 'Listings'}>
            {onTab ? (
                <div className="kartvy-list-tabs" role="tablist" aria-label={swedish ? 'Välj bostäder' : 'Choose homes'}>
                    {TABS.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === item.id}
                            className={`kartvy-list-tab${item.heart ? ' kartvy-list-tab--favorites' : ''}${tab === item.id ? ' is-active' : ''}`}
                            onClick={() => onTab(item.id)}
                        >
                            {item.heart && <HeartIcon />}
                            {swedish ? item.sv : item.en}
                            {tabCounts && <span className="kartvy-list-tab-count">{formatCount(tabCounts[item.id], swedish)}</span>}
                        </button>
                    ))}
                </div>
            ) : (
                <div className="kartvy-list-head">
                    <span className="kartvy-list-count">
                        {total} {swedish ? (total === 1 ? 'bostad' : 'bostäder') : (total === 1 ? 'home' : 'homes')}
                        {grouped && ` · ${groups.length} ${swedish ? 'områden' : 'areas'}`}
                    </span>
                </div>
            )}
            <div className="kartvy-list-scroll" ref={scrollRef}>
                {total === 0 ? (
                    <p className="kartvy-list-empty">
                        {(EMPTY_TEXT[tab] || EMPTY_TEXT.for_sale)[swedish ? 'sv' : 'en']}
                    </p>
                ) : (
                    <>
                        {rows.map((row) => (row.divider ? (
                            <button
                                type="button"
                                className={`kartvy-list-divider${folded.has(row.divider.key) ? ' is-folded' : ''}`}
                                key={`div-${row.divider.key}`}
                                aria-expanded={!folded.has(row.divider.key)}
                                title={folded.has(row.divider.key)
                                    ? (swedish ? 'Visa bostäderna' : 'Show the homes')
                                    : (swedish ? 'Dölj bostäderna' : 'Hide the homes')}
                                onClick={() => toggleFold(row.divider.key)}
                                onMouseEnter={() => onHoverArea?.(row.divider.key)}
                                onMouseLeave={() => onHoverArea?.(null)}
                            >
                                {row.divider.score != null && (
                                    <span className="kartvy-list-divider-dot" style={{ background: scoreToColor[row.divider.score] || '#8f9aab' }} />
                                )}
                                <span className="kartvy-list-divider-name">{row.divider.name}</span>
                                <span className="kartvy-list-divider-count">
                                    · {row.divider.listings.length} {swedish ? 'st' : ''}
                                </span>
                                <span className="kartvy-list-divider-line" />
                                <span className="kartvy-list-divider-chevron"><ChevronIcon /></span>
                            </button>
                        ) : renderCard(row.listing)))}
                        {hasMore && (
                            <div ref={sentinelRef} className="kartvy-list-more-sentinel" aria-hidden="true">
                                {swedish ? `Laddar fler… (${effectiveTotal - limit} kvar)` : `Loading more… (${effectiveTotal - limit} left)`}
                            </div>
                        )}
                    </>
                )}
            </div>
        </aside>
    );
};

export default React.memo(KartvyListPanel);
